import { useState } from 'react';
import DataTable from '@components/DataTable';
import {
  TableWrapper,
  SectionIcon,
  SectionRow,
  SecondaryText,
  NoDataText,
} from './styled';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import Tooltip from '@components/Tooltip';
import { Button } from '@simplywallst/ui-core';
import TeamMemberModal from './TeamMemberModal';
import { toSignificant, toTruncated } from '@/utilities/formatting';
import Media from 'react-media';
import { useTheme } from '@simplywallst/ui-core';
import type { Member } from '@/pages/CompanyReport/components/Management';
import type { TeamType } from '../../Management';
import { useTracking } from 'react-tracking';
import { ViewMode } from '@/pages/CompanyReport/enum';

interface TeamListProps {
  dataCyId?: string;
  team: Member[];
  teamType: TeamType;
  marketCapListing: number;
  currencySymbol: string;
  viewMode: ViewMode;
}

const TeamList = ({
  dataCyId,
  team,
  teamType,
  marketCapListing,
  currencySymbol,
  viewMode,
}: TeamListProps) => {
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [member, setMember] = useState(0);
  const theme = useTheme();
  const tracking = useTracking();

  const renderTenure = (member: Member) => {
    if (teamType === 'board') {
      return <SecondaryText>{member.formattedBoardTenure}</SecondaryText>;
    }
    if (teamType === 'management') {
      return <SecondaryText>{member.formattedManagementTenure}</SecondaryText>;
    }
  };

  return (
    <TableWrapper data-cy-id={`team-list-${dataCyId}`}>
      <Media query={{ minWidth: theme.mdMinRaw }}>
        {(matches) =>
          matches || viewMode === ViewMode.Print ? (
            <DataTable
              columnWidths="0.8,1.3,0.6,0.5,0.5,"
              columnTextAlignments="l,l,r,r,l,"
            >
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Tenure</th>
                  <th>Compensation</th>
                  <th>Ownership</th>
                </tr>
              </thead>
              <tbody>
                {team.map((member, n) => (
                  <tr key={`team-member-${n}`} data-cy-id={`team-member-${n}`}>
                    <td>
                      <SectionIcon>
                        <Tooltip title="View Profile">
                          <Button
                            onClick={() => {
                              setMember(n);
                              setOpenDetailModal(true);
                              tracking.trackEvent({
                                modifier: 'info',
                                action: 'click',
                              });
                            }}
                          >
                            <InformationSolid />
                          </Button>
                        </Tooltip>
                        <span>{`${member.firstName} ${member.lastName}`}</span>
                      </SectionIcon>
                    </td>
                    <td>
                      <SecondaryText>{member.shortTitle}</SecondaryText>
                    </td>
                    <td>{renderTenure(member)}</td>
                    <td>
                      {member.compensation === '' ? (
                        <NoDataText>no data</NoDataText>
                      ) : (
                        member.compensation
                      )}
                    </td>
                    <td>
                      {member.percentSharesOutstanding === '' ? (
                        <NoDataText>no data</NoDataText>
                      ) : (
                        <>
                          <span>{`${toSignificant(
                            parseFloat(member.percentSharesOutstanding),
                            2
                          )}% `}</span>
                          <br />
                          <SecondaryText>
                            {`${currencySymbol} ${toTruncated(
                              (parseFloat(member.percentSharesOutstanding) /
                                100) *
                                marketCapListing,
                              1
                            )}`}
                          </SecondaryText>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <DataTable columnWidths="1,0.6" columnTextAlignments="l,r">
              <tbody>
                {team.map((member, n) => (
                  <tr key={`team-member-${member.id}`}>
                    <td>
                      <SectionIcon>
                        <Tooltip title="View Profile">
                          <Button
                            alignIcon={'left'}
                            onClick={() => {
                              setMember(n);
                              setOpenDetailModal(true);
                            }}
                          >
                            <InformationSolid />
                          </Button>
                        </Tooltip>
                        <SectionRow>
                          <span>{`${member.firstName} ${member.lastName}`}</span>
                          <SecondaryText>{member.shortTitle}</SecondaryText>
                        </SectionRow>
                      </SectionIcon>
                    </td>
                    <td>{member.compensation}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )
        }
      </Media>
      {openDetailModal && (
        <TeamMemberModal
          member={team[member]}
          teamType={teamType}
          marketCapListing={marketCapListing}
          currencySymbol={currencySymbol}
          onClose={() => {
            setOpenDetailModal(false);
          }}
        />
      )}
    </TableWrapper>
  );
};

export default TeamList;
